/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { CustomIconProps } from '@opengeoweb/shared';
import type { CoreAppStore } from '@opengeoweb/store';
import { uiActions, uiSelectors, uiTypes } from '@opengeoweb/store';
import { Search } from '@opengeoweb/theme';
import { MapControlButton } from '@opengeoweb/webmap-react';
import { useCoreTranslation } from '../../utils/i18n';

export interface SearchControlButtonConnectProps extends CustomIconProps {
  mapId: string;
  source?: uiTypes.Source;
}

export const SearchControlButtonConnect: React.FC<
  SearchControlButtonConnectProps
> = ({ mapId, source = 'app' }: SearchControlButtonConnectProps) => {
  const { t } = useCoreTranslation();
  const dispatch = useDispatch();

  const dialogType = `${uiTypes.DialogTypes.Search}-${mapId}`;

  const currentActiveMapId = useSelector((store: CoreAppStore) =>
    uiSelectors.getDialogMapId(store, dialogType),
  );

  const isOpenInStore = useSelector((store: CoreAppStore) =>
    uiSelectors.getisDialogOpen(store, dialogType),
  );

  const openSearchDialog = React.useCallback((): void => {
    dispatch(
      uiActions.setActiveMapIdForDialog({
        type: dialogType,
        mapId,
        setOpen: currentActiveMapId !== mapId ? true : !isOpenInStore,
        source,
      }),
    );
  }, [currentActiveMapId, dialogType, dispatch, isOpenInStore, mapId, source]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement;
      if (
        (target.tagName !== 'INPUT' &&
          target.tagName !== 'TEXTAREA' &&
          event.code === 'KeyF') ||
        ((target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') &&
          event.code === 'Escape')
      ) {
        event.preventDefault();
        openSearchDialog();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return (): void => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openSearchDialog]);

  return (
    <MapControlButton
      aria-label={t('search-title-aria')}
      isActive={isOpenInStore}
      onClick={openSearchDialog}
      title={t('search-title')}
    >
      <Search />
    </MapControlButton>
  );
};
